import React, { useEffect, useMemo, useRef, useState } from "react";
import AppShell from "@/components/app/AppShell";
import { api, formatApiError } from "@/lib/api";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  MessageSquare, Search, Send, ArrowUpRight, ArrowDownLeft, RefreshCcw, CheckCheck,
  Filter, User, ExternalLink, X, CircleDot, AlertTriangle, Users,
} from "lucide-react";
import { toast } from "sonner";
import { useRealtime } from "@/hooks/useRealtime";

function fmt(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); }
  catch { return iso; }
}
function timeAgo(iso) {
  if (!iso) return "";
  try {
    const then = new Date(iso).getTime();
    const diff = Math.max(0, Date.now() - then);
    const m = Math.floor(diff / 60000);
    if (m < 1) return "just now";
    if (m < 60) return `${m}m`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h`;
    const d = Math.floor(h / 24);
    if (d < 7) return `${d}d`;
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch { return ""; }
}
function renderTemplate(text, ctx) {
  if (!text) return "";
  const map = {
    "contact.first_name": ctx.first_name || (ctx.name || "").split(" ")[0] || "there",
    "contact.last_name": ctx.last_name || ((ctx.name || "").split(" ").slice(1).join(" ")) || "",
    "contact.name": ctx.name || "",
    "user.first_name": ctx.actor_first_name || "",
    "user.name": ctx.actor_name || "",
    "organization.name": "Credit Quack",
  };
  return text.replace(/\{\{\s*([\w.]+)\s*\}\}/g, (m, k) => (k in map ? map[k] : m));
}

export default function AdminSMS() {
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const [conversations, setConversations] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [q, setQ] = useState(params.get("q") || "");
  const [selectedPhone, setSelectedPhone] = useState(params.get("phone") || null);
  const [thread, setThread] = useState({ items: [], loading: false, error: null });
  const [templates, setTemplates] = useState([]);
  const [status, setStatus] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [busy, setBusy] = useState(false);
  const [clientFilter, setClientFilter] = useState("all"); // all | linked | unlinked
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const threadEndRef = useRef(null);

  const selectedConv = useMemo(
    () => conversations.find((c) => c.phone === selectedPhone) || null,
    [conversations, selectedPhone],
  );

  const loadConversations = async () => {
    try {
      const { data: convData } = await api.get("/close-sms/conversations", {
        params: { limit: 100, ...(q ? { q } : {}) },
      });
      let list = convData.conversations || [];
      if (clientFilter === "linked") list = list.filter((c) => c.client_id);
      else if (clientFilter === "unlinked") list = list.filter((c) => !c.client_id);
      setConversations(list);
      // Auto-select first if none picked
      if (!selectedPhone && list.length > 0) {
        setSelectedPhone(list[0].phone);
      }
    } catch (e) {
      toast.error("Couldn't load conversations");
    }
    try {
      const { data: unread } = await api.get("/close-sms/inbox", { params: { limit: 1, unread_only: true } });
      setUnreadCount(unread.unread_count || 0);
    } catch {}
  };

  const loadStatus = async () => {
    try {
      const [s, t] = await Promise.all([
        api.get("/close-sms/status"),
        api.get("/close-sms/templates"),
      ]);
      setStatus(s.data);
      setTemplates(t.data.templates || []);
    } catch {}
  };

  const loadThread = async (phone) => {
    if (!phone) { setThread({ items: [], loading: false, error: null }); return; }
    setThread((t) => ({ ...t, loading: true, error: null }));
    try {
      const { data } = await api.get("/close-sms/thread-by-phone", { params: { phone, limit: 100 } });
      setThread({ items: data.items || [], loading: false, error: data.error || null, anchor_inbox_id: data.anchor_inbox_id, close_lead_id: data.close_lead_id, client_id: data.client_id, client_name: data.client_name });
      setTimeout(() => threadEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" }), 60);
      // Mark all inbox rows for this phone as read
      try {
        const { data: inboxRows } = await api.get("/close-sms/inbox", { params: { phone, limit: 100, unread_only: true } });
        for (const row of (inboxRows.items || [])) {
          await api.post(`/close-sms/inbox/${row.id}/read`);
        }
        if ((inboxRows.items || []).length > 0) loadConversations();
      } catch {}
    } catch (e) {
      setThread({ items: [], loading: false, error: formatApiError(e.response?.data?.detail) || "Failed to load thread" });
    }
  };

  useEffect(() => { loadStatus(); }, []);
  useEffect(() => { loadConversations(); /* eslint-disable-next-line */ }, [q, clientFilter]);
  // Near-real-time refresh: poll every 3s while inbox is open
  useEffect(() => {
    const id = setInterval(() => {
      loadConversations();
      if (selectedPhone) loadThread(selectedPhone);
    }, 3000);
    return () => clearInterval(id);
    /* eslint-disable-next-line */
  }, [q, clientFilter, selectedPhone]);
  useEffect(() => {
    if (selectedPhone) {
      params.set("phone", selectedPhone);
      setParams(params, { replace: true });
    }
    loadThread(selectedPhone);
    /* eslint-disable-next-line */
  }, [selectedPhone]);

  useRealtime((evt) => {
    if (evt?.type === "notification" && evt.data?.kind === "sms_inbound") {
      loadConversations();
      if (selectedPhone && evt.data?.extra?.from_number === selectedPhone) {
        loadThread(selectedPhone);
      }
    }
  });

  const send = async () => {
    if (!replyText.trim()) return toast.error("Type a message first");
    if (!selectedConv) return toast.error("Pick a conversation");
    setBusy(true);
    try {
      const payload = { text: replyText.trim() };
      if (thread.anchor_inbox_id) payload.inbox_id = thread.anchor_inbox_id;
      else if (selectedConv.client_id) payload.client_id = selectedConv.client_id;
      await api.post("/close-sms/send", payload);
      toast.success("SMS sent");
      setReplyText("");
      setSelectedTemplateId("");
      await Promise.all([loadThread(selectedPhone), loadConversations()]);
    } catch (e) {
      const detail = formatApiError(e.response?.data?.detail) || e.message;
      toast.error(detail, { duration: 8000, description: "SMS did not send." });
    } finally { setBusy(false); }
  };

  const applyTemplate = (tid) => {
    setSelectedTemplateId(tid);
    const tpl = templates.find((t) => t.id === tid);
    if (tpl) {
      const rendered = renderTemplate(tpl.text, {
        name: selectedConv?.client_name || "",
        first_name: (selectedConv?.client_name || "").split(" ")[0],
      });
      setReplyText(rendered);
    }
  };

  const markAllRead = async () => {
    try {
      const { data } = await api.post("/close-sms/inbox/read-all");
      toast.success(`Marked ${data.marked} as read`);
      loadConversations();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };

  const closeTemplates = templates.filter((t) => t.source === "close");
  const localTemplates = templates.filter((t) => t.source === "local");
  const charCount = replyText.length;
  const segments = Math.max(1, Math.ceil(charCount / 160));

  return (
    <AppShell>
      <div className="mb-4 cq-in">
        <div className="flex items-center gap-3 flex-wrap">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-emerald-600">Admin Console</p>
            <h1 className="font-display text-4xl sm:text-5xl font-black text-slate-900">SMS Inbox</h1>
            <p className="text-slate-500 mt-1">
              Two-way replies via Close · {status?.org_name || "—"} · syncs every 60s
              {status?.numbers?.[0]?.number && <> · from <span className="font-mono">{status.numbers[0].number}</span></>}
            </p>
          </div>
          {unreadCount > 0 && (
            <Badge className="bg-emerald-500 text-white border-0 text-sm px-3 py-1" data-testid="sms-unread-badge">
              {unreadCount} unread
            </Badge>
          )}
          <div className="sm:ml-auto flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={() => { loadConversations(); loadThread(selectedPhone); }} data-testid="sms-refresh">
              <RefreshCcw size={14} className="mr-1" /> Refresh
            </Button>
            {unreadCount > 0 && (
              <Button size="sm" variant="outline" onClick={markAllRead} data-testid="sms-mark-all-read">
                <CheckCheck size={14} className="mr-1" /> Mark all read
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-4 h-[calc(100vh-14rem)] min-h-[600px]">
        {/* Conversations list */}
        <Card className="p-3 rounded-2xl border-slate-100 flex flex-col min-h-0" data-testid="conversations-panel">
          <div className="mb-3 space-y-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
              <Input
                placeholder="Search messages, names, numbers…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="pl-9 h-9 rounded-xl"
                data-testid="sms-search"
              />
              {q && (
                <button onClick={() => setQ("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" aria-label="Clear search">
                  <X size={14} />
                </button>
              )}
            </div>
            <div className="flex gap-1 rounded-xl bg-slate-100 p-1">
              {[
                { v: "all", label: `All · ${conversations.length}` },
                { v: "linked", label: "Linked" },
                { v: "unlinked", label: "Unlinked" },
              ].map((b) => (
                <button
                  key={b.v}
                  onClick={() => setClientFilter(b.v)}
                  className={`flex-1 rounded-lg py-1 text-xs font-semibold transition ${clientFilter === b.v ? "bg-white text-slate-900 shadow" : "text-slate-500"}`}
                  data-testid={`filter-${b.v}`}
                >{b.label}</button>
              ))}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto -mx-3 px-3 space-y-1" data-testid="conversations-list">
            {conversations.length === 0 && (
              <p className="text-sm text-slate-400 text-center py-8">No conversations match.</p>
            )}
            {conversations.map((c) => {
              const isSelected = c.phone === selectedPhone;
              const hasUnread = c.unread > 0;
              const shownName = c.display_name || c.client_name;
              const isUnknown = c.unknown_contact && !c.client_id;
              return (
                <button
                  key={c.phone}
                  onClick={() => setSelectedPhone(c.phone)}
                  className={`w-full text-left rounded-xl p-3 transition border ${isSelected ? "bg-emerald-50 border-emerald-300 shadow-sm" : hasUnread ? "bg-emerald-50/40 border-emerald-100 hover:bg-emerald-50/70" : "bg-white border-slate-100 hover:bg-slate-50"}`}
                  data-testid={`conv-${c.phone}`}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    {hasUnread && <CircleDot size={12} className="text-emerald-500 shrink-0" />}
                    <span className={`font-semibold truncate ${hasUnread ? "text-slate-900" : "text-slate-700"}`}>
                      {shownName || c.phone}
                    </span>
                    {isUnknown && (
                      <Badge className="bg-red-100 text-red-700 border-0 text-[9px] uppercase tracking-widest" data-testid={`conv-${c.phone}-unknown`}>
                        Unknown contact
                      </Badge>
                    )}
                    {!c.client_id && !isUnknown && (
                      <Badge className="bg-slate-100 text-slate-500 border-0 text-[9px] uppercase tracking-widest">Close only</Badge>
                    )}
                    <span className="text-xs text-slate-400 ml-auto shrink-0">{timeAgo(c.last_date_created || c.last_at)}</span>
                  </div>
                  <p className={`text-sm mt-0.5 truncate ${hasUnread ? "text-slate-800 font-medium" : "text-slate-500"}`}>{c.last_text || "—"}</p>
                  {shownName && (
                    <p className="text-[10px] text-slate-400 mt-0.5 font-mono truncate">{c.phone}{c.close_company ? ` · ${c.close_company}` : ""}</p>
                  )}
                </button>
              );
            })}
          </div>
        </Card>

        {/* Thread + reply */}
        <Card className="p-0 rounded-2xl border-slate-100 flex flex-col min-h-0 overflow-hidden" data-testid="thread-panel">
          {!selectedConv && (
            <div className="flex-1 flex items-center justify-center text-slate-400">
              <div className="text-center">
                <MessageSquare size={48} className="mx-auto mb-2 text-slate-300" />
                <p className="font-semibold">Pick a conversation on the left</p>
                <p className="text-xs">or hit refresh if you're expecting a new reply</p>
              </div>
            </div>
          )}
          {selectedConv && (
            <>
              {/* Header */}
              <div className="px-5 py-4 border-b border-slate-100 bg-white flex items-center gap-3 flex-wrap">
                <div className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center">
                  <User size={18} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h2 className="font-display text-xl font-bold text-slate-900">{selectedConv.client_name || selectedConv.phone}</h2>
                    {!selectedConv.client_id && <Badge className="bg-slate-100 text-slate-500 border-0 text-[9px] uppercase tracking-widest">Unlinked</Badge>}
                  </div>
                  <p className="text-xs text-slate-500 font-mono">{selectedConv.phone}</p>
                </div>
                {selectedConv.client_id && (
                  <Button size="sm" variant="outline" onClick={() => nav(`/admin/clients/${selectedConv.client_id}`)} data-testid="open-client-page">
                    <ExternalLink size={14} className="mr-1" /> Open client
                  </Button>
                )}
              </div>

              {/* Thread */}
              <div className="flex-1 overflow-y-auto px-5 py-4 space-y-2 bg-slate-50" data-testid="thread-messages">
                {thread.loading && <p className="text-sm text-slate-400 text-center py-6">Loading thread…</p>}
                {thread.error && (
                  <div className="rounded-xl bg-red-50 border border-red-200 p-3 text-sm text-red-700 flex gap-2 items-start" data-testid="thread-error">
                    <AlertTriangle size={16} className="mt-0.5" />
                    <div>
                      <p className="font-semibold">Couldn't load thread from Close</p>
                      <p className="text-xs mt-0.5">{thread.error}</p>
                    </div>
                  </div>
                )}
                {!thread.loading && thread.items.length === 0 && !thread.error && (
                  <p className="text-sm text-slate-400 text-center py-8">No messages yet.</p>
                )}
                {[...(thread.items || [])].reverse().map((m) => {
                  const outbound = m.direction === "outbound";
                  return (
                    <div key={m.id} className={`flex ${outbound ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[80%] rounded-2xl px-4 py-2 ${outbound ? "bg-emerald-500 text-white" : "bg-white border border-slate-100 text-slate-800"}`} data-testid={`msg-${m.id}`}>
                        <p className="text-sm whitespace-pre-wrap">{m.text}</p>
                        <div className={`text-[10px] mt-1 flex items-center gap-1 ${outbound ? "text-white/70 justify-end" : "text-slate-400"}`}>
                          {outbound ? <ArrowUpRight size={10} /> : <ArrowDownLeft size={10} />}
                          <span>{fmt(m.date_sent || m.date_created)}</span>
                          {m.status && <span className="uppercase tracking-widest">· {m.status}</span>}
                        </div>
                        {m.error && <p className={`text-[10px] mt-1 ${outbound ? "text-red-100" : "text-red-500"}`}>⚠ {m.error}</p>}
                      </div>
                    </div>
                  );
                })}
                <div ref={threadEndRef} />
              </div>

              {/* Reply composer */}
              <div className="border-t border-slate-100 p-4 bg-white space-y-2">
                {templates.length > 0 && (
                  <Select value={selectedTemplateId} onValueChange={applyTemplate}>
                    <SelectTrigger className="h-8 text-xs" data-testid="reply-template-picker">
                      <SelectValue placeholder={`${templates.length} templates — pick one to prefill`} />
                    </SelectTrigger>
                    <SelectContent className="max-h-64">
                      {closeTemplates.length > 0 && (
                        <>
                          <div className="px-2 pt-2 pb-1 text-[10px] uppercase tracking-widest font-bold text-slate-400">From Close</div>
                          {closeTemplates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                        </>
                      )}
                      {localTemplates.length > 0 && (
                        <>
                          <div className="px-2 pt-2 pb-1 text-[10px] uppercase tracking-widest font-bold text-amber-600">Client Quack Templates</div>
                          {localTemplates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                        </>
                      )}
                    </SelectContent>
                  </Select>
                )}
                <div className="flex items-end gap-2">
                  <Textarea
                    rows={2}
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); send(); }
                    }}
                    placeholder={`Reply to ${selectedConv.client_name || selectedConv.phone}… (⌘/Ctrl + Enter to send)`}
                    className="flex-1 font-mono text-sm resize-none"
                    data-testid="reply-text"
                  />
                  <Button
                    onClick={send}
                    disabled={busy || !replyText.trim()}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white h-12"
                    data-testid="reply-send"
                  >
                    <Send size={14} className="mr-1" /> {busy ? "…" : "Send"}
                  </Button>
                </div>
                <div className="flex items-center gap-3 text-[10px] text-slate-400">
                  <span>{charCount} chars · {segments} segment{segments > 1 ? "s" : ""}</span>
                  <span>·</span>
                  <span>Sent via Close ({status?.numbers?.[0]?.number || "—"})</span>
                </div>
              </div>
            </>
          )}
        </Card>
      </div>
    </AppShell>
  );
}
